import React, { useState, useEffect } from 'react';
import CheckboxField from '../shared/CheckboxField';

// Temporary placeholder for icons - replace with your preferred icon library
const CheckCircleIcon = ({ className }) => <span className={className}>✓</span>;
const ExclamationTriangleIcon = ({ className }) => <span className={className}>⚠️</span>;
const PaperAirplaneIcon = ({ className }) => <span className={className}>📤</span>;
const PrinterIcon = ({ className }) => <span className={className}>🖨️</span>;
const DocumentDuplicateIcon = ({ className }) => <span className={className}>📄</span>;
const EyeIcon = ({ className }) => <span className={className}>👁️</span>;
const PencilIcon = ({ className }) => <span className={className}>✏️</span>;

/**
 * ReviewAndSubmit - Final form section for review and submission of safety report
 * 
 * This section provides:
 * - Complete summary of all entered data
 * - Final validation checks
 * - Submission confirmation
 * - Offline/online submission handling
 * - Success/error states
 * - Report number generation
 * 
 * PWA Features:
 * - Offline submission queuing
 * - Background sync when online
 * - Local storage backup
 */

const FormSection = ({ title, children, icon }) => (
  <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
    <div className="flex items-center space-x-3 mb-6">
      {icon}
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
    </div>
    {children}
  </div>
);

const ReviewAndSubmit = ({ 
  formData, 
  updateFormData, 
  errors = {}, 
  loading = false,
  onSubmit,
  onEditSection,
  isOnline = true,
  validationSummary = {},
  submissionStatus = null // null, 'submitting', 'success', 'error'
}) => {
  const [confirmationChecks, setConfirmationChecks] = useState({
    informationAccurate: false,
    understandProcess: false,
    authorizeSubmission: false
  });
  
  const [showFullSummary, setShowFullSummary] = useState(false);

  // Check if all required confirmations are completed
  const allConfirmed = Object.values(confirmationChecks).every(Boolean);
  const hasValidationErrors = Object.keys(errors).length > 0;
  const hasValidationWarnings = validationSummary?.warnings?.length > 0;

  const handleConfirmationChange = (field, value) => {
    setConfirmationChecks(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (allConfirmed && !hasValidationErrors && onSubmit) {
      onSubmit(formData);
    }
  };

  // Generate summary sections
  const getSummaryData = () => {
    const sections = [
      {
        title: 'Reporter Information',
        key: 'reporter',
        icon: '👤',
        data: [
          { label: 'Name', value: formData.reporter_name },
          { label: 'Position', value: formData.reporter_position },
          { label: 'Employee ID', value: formData.reporter_employee_id },
          { label: 'Department', value: formData.department_name },
          { label: 'Email', value: formData.reporter_email },
          { label: 'Phone', value: formData.reporter_phone },
          { label: 'Confidential', value: formData.is_confidential ? 'Yes' : 'No' }
        ]
      },
      {
        title: 'Event Details',
        key: 'event',
        icon: '📋',
        data: [
          { label: 'Title', value: formData.event_title },
          { label: 'Date & Time', value: formData.event_date_time ? new Date(formData.event_date_time).toLocaleString() : '' },
          { label: 'Location', value: formData.location_name },
          { label: 'Category', value: formData.event_category_name },
          { label: 'Type', value: formData.event_type_name },
          { label: 'Aircraft Registration', value: formData.aircraft_registration },
          { label: 'Work Order', value: formData.work_order_number }
        ]
      },
      {
        title: 'Event Description',
        key: 'description',
        icon: '📝',
        data: [
          { label: 'Description', value: formData.event_description, type: 'text' },
          { label: 'Immediate Actions', value: formData.immediate_actions_taken, type: 'text' },
          { label: 'Potential Consequences', value: formData.potential_consequences, type: 'text' },
          { label: 'Near Miss', value: formData.is_near_miss ? 'Yes' : 'No' }
        ]
      },
      {
        title: 'Safety Impact',
        key: 'impact',
        icon: '⚠️',
        data: [
          { label: 'Injuries Occurred', value: formData.injuries_occurred ? 'Yes' : 'No' },
          { label: 'Property Damage', value: formData.property_damage ? 'Yes' : 'No' },
          { label: 'Spill Occurred', value: formData.spill_occurred ? 'Yes' : 'No' },
          { label: 'Regulatory Reporting Required', value: formData.regulatory_reporting_required ? 'Yes' : 'No' },
          { label: 'Damage Cost', value: formData.estimated_damage_cost ? `$${formData.estimated_damage_cost}` : '' }
        ]
      },
      {
        title: 'Environment & Equipment',
        key: 'environment',
        icon: '🔧',
        data: [
          { label: 'Weather Conditions', value: formData.weather_conditions },
          { label: 'Lighting', value: formData.lighting_conditions },
          { label: 'Surface Conditions', value: formData.surface_conditions },
          { label: 'Equipment Involved', value: formData.equipment_involved, type: 'text' },
          { label: 'Contractor Involved', value: formData.contractor_involved ? 'Yes' : 'No' }
        ]
      }
    ].filter(section => section.data.some(item => item.value));

    return sections;
  };

  const summaryData = getSummaryData();

  if (submissionStatus === 'success') {
    return <SubmissionSuccess formData={formData} />;
  }

  return (
    <FormSection 
      title="Review & Submit" 
      icon={<PaperAirplaneIcon className="h-6 w-6 text-blue-600" />}
    >
      <div className="space-y-6">
        {/* Validation Summary */}
        {(hasValidationErrors || hasValidationWarnings) && (
          <ValidationSummary 
            errors={errors} 
            warnings={validationSummary.warnings} 
            onEditSection={onEditSection}
          />
        )}

        {/* Offline Status */}
        {!isOnline && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Offline Mode</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  You're currently offline. Your report will be saved locally and submitted automatically when you reconnect to the internet.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Summary Sections */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Report Summary</h3>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setShowFullSummary(!showFullSummary)}
                  className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-full hover:bg-blue-200"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  {showFullSummary ? 'Hide Details' : 'Show All Details'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {summaryData.map((section, index) => (
              <SummarySection 
                key={section.key}
                section={section}
                onEdit={() => onEditSection && onEditSection(index + 1)}
                showFull={showFullSummary}
              />
            ))}
          </div>
        </div>

        {/* Final Confirmations */}
        <div className="border border-blue-200 rounded-lg p-6 bg-blue-50">
          <h3 className="text-lg font-medium text-blue-900 mb-4">Final Confirmation</h3>
          
          <div className="space-y-4">
            <CheckboxField
              label="I confirm that the information provided is accurate and complete to the best of my knowledge"
              name="informationAccurate"
              checked={confirmationChecks.informationAccurate}
              onChange={(updates) => setConfirmationChecks(prev => ({ ...prev, ...updates }))}
              disabled={loading}
              required={true}
            />
            
            <CheckboxField
              label="I understand that this report will be reviewed by safety management and may result in investigation or corrective actions"
              name="understandProcess"
              checked={confirmationChecks.understandProcess}
              onChange={(updates) => setConfirmationChecks(prev => ({ ...prev, ...updates }))}
              disabled={loading}
              required={true}
            />
            
            <CheckboxField
              label="I authorize the submission of this safety report"
              name="authorizeSubmission"
              checked={confirmationChecks.authorizeSubmission}
              onChange={(updates) => setConfirmationChecks(prev => ({ ...prev, ...updates }))}
              disabled={loading}
              required={true}
            />
          </div>

          {formData.is_confidential && (
            <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded">
              <p className="text-sm text-yellow-800">
                <strong>Confidential Report:</strong> Your identity will be protected. Only safety management will know who submitted this report.
              </p>
            </div>
          )}
        </div>

        {/* Submission Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              <PrinterIcon className="h-4 w-4 mr-2" />
              Print Draft
            </button>
            
            <button
              type="button"
              onClick={() => {/* Save draft logic */}}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
              Save Draft
            </button>
          </div>
          
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allConfirmed || hasValidationErrors || loading || submissionStatus === 'submitting'}
            className={`inline-flex items-center px-6 py-3 rounded-lg text-white font-medium ${
              !allConfirmed || hasValidationErrors || loading || submissionStatus === 'submitting'
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
            }`}
          >
            {submissionStatus === 'submitting' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isOnline ? 'Submitting...' : 'Saving...'}
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                {isOnline ? 'Submit Report' : 'Save for Later Submission'}
              </>
            )}
          </button>
        </div>

        {/* Submission Status */}
        {submissionStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-red-800">Submission Error</h4>
                <p className="text-sm text-red-700 mt-1">
                  There was an error submitting your report. Please try again or save as draft.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </FormSection>
  );
};

// Helper component for validation summary
const ValidationSummary = ({ errors, warnings = [], onEditSection }) => (
  <div className="space-y-3">
    {Object.keys(errors).length > 0 && (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-800">Required Fields Missing</h4>
            <div className="mt-2 text-sm text-red-700">
              <ul className="list-disc list-inside space-y-1">
                {Object.entries(errors).map(([field, error]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {warnings.length > 0 && (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-yellow-800">Recommendations</h4>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc list-inside space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Helper component for each summary section
const SummarySection = ({ section, onEdit, showFull }) => (
  <div className="p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center space-x-3">
        <span className="text-xl">{section.icon}</span>
        <h4 className="text-lg font-medium text-gray-900">{section.title}</h4>
      </div>
      {onEdit && (
        <button
          onClick={onEdit}
          className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-full hover:bg-blue-200"
        >
          <PencilIcon className="h-3 w-3 mr-1" />
          Edit
        </button>
      )}
    </div>
    
    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
      {section.data.filter(item => item.value).map((item, index) => (
        <div key={index} className={item.type === 'text' ? 'md:col-span-2' : ''}>
          <dt className="text-sm font-medium text-gray-600">{item.label}:</dt>
          <dd className={`text-sm text-gray-900 ${item.type === 'text' ? 'mt-1' : 'mt-0'}`}>
            {item.type === 'text' ? (
              <div className={`${showFull ? '' : 'line-clamp-2'}`}>
                {item.value}
              </div>
            ) : (
              item.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

// Success component
const SubmissionSuccess = ({ formData }) => (
  <FormSection 
    title="Report Submitted Successfully" 
    icon={<CheckCircleIcon className="h-6 w-6 text-green-600" />}
  >
    <div className="text-center py-8">
      <CheckCircleIcon className="mx-auto h-16 w-16 text-green-600 mb-4" />
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        Safety Report Submitted Successfully
      </h3>
      <p className="text-gray-600 mb-6">
        Your safety report has been submitted and assigned number:{' '}
        <span className="font-mono font-semibold text-blue-600">
          {formData.report_number || 'Generating...'}
        </span>
      </p>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-blue-900 mb-2">What happens next?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Safety management will review your report within 24 hours</li>
          <li>• You will receive email updates on the progress</li>
          <li>• Investigation may be initiated if required</li>
          {formData.is_confidential && (
            <li>• Your identity will remain confidential throughout the process</li>
          )}
        </ul>
      </div>
      
      <div className="flex items-center justify-center space-x-4">
        <button
          onClick={() => window.print()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          <PrinterIcon className="h-4 w-4 mr-2" />
          Print Receipt
        </button>
        
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Submit Another Report
        </button>
      </div>
    </div>
  </FormSection>
);

export default ReviewAndSubmit;
